import React, { useState } from 'react';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import CircularProgress from '@mui/material/CircularProgress';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { StackedDialog } from '../../utilities/ModalStackContext';
import { StateManager } from '../../utilities/stateManager.js';
import firebase from '../../utilities/firebase.js';

const db = firebase.firestore();

// Colored status chip so staff can see at a glance whether a flagged line is
// already done/ordered before removing it.
const PART_STATUS_COLORS = {
  pending: 'default',
  approved: 'info',
  inbound: 'info',
  complete: 'success',
  returning: 'warning',
  returned: 'warning',
  declined: 'error',
};

const statusChipFor = (kind, item) => {
  const status = item.status || 'pending';
  const label = status.charAt(0).toUpperCase() + status.slice(1);
  const color = kind === 'part'
    ? (PART_STATUS_COLORS[status] || 'default')
    : (status === 'complete' ? 'success' : 'default');
  return { label, color };
};

// Reviews the duplicate groups the AI flagged and lets staff remove redundant
// lines inline. Deleting a line removes its Firestore doc and drops it from the
// order's selected-id list; the SO modal's realtime listeners refresh order.services
// / order.parts, so no local order mutation is needed here beyond StateManager.
export default function DuplicatesDialog({ open, onClose, groups = [], order }) {
  // ids we've already removed this session — struck through and no longer actionable
  const [removed, setRemoved] = useState({});
  const [removingId, setRemovingId] = useState(null);

  const servicesById = Object.fromEntries((order?.services || []).map(s => [s.id, s]));
  const partsById = Object.fromEntries((order?.parts || []).map(p => [p.id, p]));

  const describe = (kind, id) => {
    if (kind === 'part') {
      const p = partsById[id];
      if (!p) return { name: '(removed)', meta: '', status: null };
      const bits = [];
      if (p.tag) bits.push(p.tag);
      if (p.quantity != null) bits.push(`qty ${p.quantity}`);
      if (p.cost != null) bits.push(`$${p.cost}`);
      if (p.orderDate) bits.push(`ordered ${p.orderDate}`);
      else if (p.arrivalDate) bits.push(`arrived ${p.arrivalDate}`);
      return {
        name: p.name || p.memo || '(no description)',
        meta: bits.join(' · '),
        status: statusChipFor('part', p),
      };
    }
    const s = servicesById[id];
    if (!s) return { name: '(removed)', meta: '', status: null };
    const bits = [];
    if (s.tag) bits.push(s.tag);
    if (s.time != null) bits.push(`${s.time} hr`);
    if (s.mechanic) bits.push(`by ${s.mechanic}`);
    return {
      name: s.name || '(no description)',
      meta: bits.join(' · '),
      status: statusChipFor('service', s),
    };
  };

  const handleRemove = async (kind, id) => {
    setRemovingId(id);
    try {
      const collection = kind === 'part' ? 'parts' : 'services';
      const selectedField = kind === 'part' ? 'selectedPartIds' : 'selectedServiceIds';

      await db.collection(collection).doc(id).delete();

      const currentSelected = order[selectedField]
        || (order[collection] || []).map(i => i.id);
      const updatedSelected = currentSelected.filter(x => x !== id);

      await db.doc(`orders/${order.stock}`).update({ [selectedField]: updatedSelected });
      StateManager.updateOrder({ [selectedField]: updatedSelected });

      setRemoved(prev => ({ ...prev, [id]: true }));
      StateManager.setAlertAndOpen('Duplicate removed', 'success');
    } catch (error) {
      console.error('Error removing duplicate:', error);
      StateManager.setAlertAndOpen(`Failed to remove: ${error.message}`, 'error');
    } finally {
      setRemovingId(null);
    }
  };

  return (
    <StackedDialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Duplicate Services & Parts</DialogTitle>
      <DialogContent dividers>
        {groups.length === 0 ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2, color: 'success.dark' }}>
            <CheckCircleOutlineIcon color="success" />
            <Typography>No duplicate services or parts were found.</Typography>
          </Box>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              The AI flagged the following as likely duplicates. Review each group and remove the
              redundant line(s) — keep one per group.
            </Typography>
            {groups.map((group, gi) => (
              <Box key={gi} sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Chip
                    label={group.kind === 'part' ? 'Parts' : 'Services'}
                    size="small"
                    color={group.kind === 'part' ? 'info' : 'primary'}
                  />
                  <Typography variant="body2" color="text.secondary">{group.reason}</Typography>
                </Box>
                {group.ids.map(id => {
                  const { name, meta, status } = describe(group.kind, id);
                  const isRemoved = removed[id];
                  return (
                    <Box
                      key={id}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1,
                        py: 0.75,
                        px: 1,
                        borderBottom: '1px solid #eee',
                        opacity: isRemoved ? 0.5 : 1,
                      }}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <Typography
                            variant="body2"
                            sx={{ textDecoration: isRemoved ? 'line-through' : 'none' }}
                          >
                            {name}
                          </Typography>
                          {status && (
                            <Chip
                              label={status.label}
                              size="small"
                              color={status.color}
                              variant={status.color === 'default' ? 'outlined' : 'filled'}
                              sx={{ height: 18, fontSize: '0.65rem' }}
                            />
                          )}
                        </Box>
                        {meta && (
                          <Typography variant="caption" color="text.secondary">{meta}</Typography>
                        )}
                      </Box>
                      <Button
                        size="small"
                        color="error"
                        variant="outlined"
                        disabled={isRemoved || removingId === id}
                        startIcon={
                          removingId === id
                            ? <CircularProgress size={14} color="inherit" />
                            : <DeleteOutlineIcon fontSize="small" />
                        }
                        onClick={() => handleRemove(group.kind, id)}
                        sx={{ flexShrink: 0 }}
                      >
                        {isRemoved ? 'Removed' : 'Remove'}
                      </Button>
                    </Box>
                  );
                })}
                {gi < groups.length - 1 && <Divider sx={{ mt: 2 }} />}
              </Box>
            ))}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Done</Button>
      </DialogActions>
    </StackedDialog>
  );
}
